import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PublicLayout } from "@/components/public/PublicLayout";
import { publicApi } from "@/lib/api";
import { useBranding } from "@/contexts/BrandingContext";

/**
 * MINIMALIST — Typography-first, generous whitespace, monochrome with single accent.
 */
export default function MinimalistTemplate() {
    const { branding } = useBranding();
    const [data, setData] = useState({ next: null, last: null, players: [], posts: [], standings: [] });

    useEffect(() => {
        Promise.all([
            publicApi.nextMatch().catch(() => null),
            publicApi.lastMatch().catch(() => null),
            publicApi.players().catch(() => []),
            publicApi.posts({ limit: 4 }).catch(() => []),
            publicApi.standings().catch(() => []),
        ]).then(([next, last, players, posts, standings]) =>
            setData({ next, last, players, posts, standings })
        );
    }, []);

    const primary = branding.primary_color || "#f5dc4c";

    return (
        <PublicLayout>
            <div data-testid="template-minimalist" className="bg-white text-black" style={{ fontFamily: "'Manrope', sans-serif" }}>
                {/* Massive typography hero */}
                <section className="container-x py-32 md:py-48 text-center">
                    <div className="text-xs uppercase tracking-[0.3em] mb-8" style={{ color: primary }}>EST. {new Date().getFullYear() - 5}</div>
                    <h1 className="text-7xl md:text-9xl font-extrabold tracking-tighter leading-none uppercase mb-8" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                        {branding.short_name || branding.name}
                    </h1>
                    <p className="text-lg text-neutral-600 max-w-xl mx-auto">{branding.description || "Az ama öz."}</p>
                    <div className="mt-12 inline-block h-px w-32" style={{ backgroundColor: primary }}></div>
                </section>

                {/* Match strip */}
                {(data.next || data.last) && (
                    <section className="border-y border-neutral-200 py-16">
                        <div className="container-x grid grid-cols-1 md:grid-cols-2 gap-12">
                            {data.next && (
                                <div>
                                    <div className="text-xs uppercase tracking-[0.3em] mb-3" style={{ color: primary }}>SIRADAKİ</div>
                                    <div className="text-3xl font-bold mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{data.next.home_team} — {data.next.away_team}</div>
                                    <div className="text-sm text-neutral-500">{(data.next.match_date || "").slice(0, 10)} · {data.next.venue}</div>
                                </div>
                            )}
                            {data.last && (
                                <div>
                                    <div className="text-xs uppercase tracking-[0.3em] mb-3" style={{ color: primary }}>SON SONUÇ</div>
                                    <div className="text-3xl font-bold mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{data.last.home_team} {data.last.home_score}–{data.last.away_score} {data.last.away_team}</div>
                                    <div className="text-sm text-neutral-500">{(data.last.match_date || "").slice(0, 10)}</div>
                                </div>
                            )}
                        </div>
                    </section>
                )}

                {/* Posts list — text-driven */}
                <section className="container-x py-24">
                    <div className="text-xs uppercase tracking-[0.3em] mb-10" style={{ color: primary }}>YAZILAR</div>
                    <div className="divide-y divide-neutral-200">
                        {data.posts.map((p) => (
                            <Link key={p.id} to={`/haberler/${p.slug}`} className="block py-8 group">
                                <div className="flex items-baseline justify-between gap-6">
                                    <div className="flex-1">
                                        <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">{p.category}</div>
                                        <h3 className="text-2xl md:text-3xl font-bold group-hover:underline" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{p.title}</h3>
                                    </div>
                                    <div className="text-xs text-neutral-500 whitespace-nowrap">{(p.published_at || "").slice(0, 10)}</div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>

                {/* Roster numbers */}
                <section className="bg-black text-white py-24">
                    <div className="container-x">
                        <div className="text-xs uppercase tracking-[0.3em] mb-10" style={{ color: primary }}>KADRO</div>
                        <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-8 gap-6">
                            {data.players.slice(0, 16).map((p) => (
                                <Link key={p.id} to={`/oyuncular/${p.slug}`} className="text-center group">
                                    <div className="text-5xl font-extrabold mb-2 group-hover:text-yellow-400 transition-colors" style={{ fontFamily: "'Bebas Neue', sans-serif", color: primary }}>
                                        {p.jersey_number || "?"}
                                    </div>
                                    <div className="text-xs uppercase tracking-widest">{p.name}</div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            </div>
        </PublicLayout>
    );
}
